import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import DailyNoteCategoryDropdown from '../dailyNote/DailyNoteCategoryDropdown';
import DailyNoteLockedPreview from '../dailyNote/DailyNoteLockedPreview';
import { isDefaultDailyCategoryName } from '../../utils/dailyNoteCategoryEdit';
import type { DailyNoteCategoryRow, DailyWeekNoteDraft } from '../../types/dailyRecordNote';
import {
  DASHBOARD_WEEKDAY_EN,
  formatDashboardWeekMmDd,
  weekDateIsosFromMonday,
} from '../../utils/dashboardSemesterWeek';
import { toISODate } from '../../utils/date';
import {
  createDailyWeekDraftNote,
  customDailyCategories,
  dateHasSavedWeekNoteContent,
  defaultDailyCategoryId,
  firstCustomDailyCategory,
  generalNoteForDate,
  patchStickyNote,
  setGeneralBodyForDate,
  shouldAppendWeeklyStickyComposer,
  stickyNotesForDate,
  upsertStickyNote,
} from '../../utils/dailyRecordNotesWeekState';

type Props = {
  mondayIso: string;
  holidayIsoSet: ReadonlySet<string>;
  holidayNoteByIso?: Readonly<Record<string, string>>;
  daysVertical?: boolean;
  notes: DailyWeekNoteDraft[];
  savedNotes?: DailyWeekNoteDraft[];
  categories: DailyNoteCategoryRow[];
  onChangeNotes: (notes: DailyWeekNoteDraft[]) => void;
  contentsHidden?: boolean;
  onUnlockRequest?: () => void;
  interactive?: boolean;
};

function holidayNameFor(
  iso: string | undefined,
  holidayIsoSet: ReadonlySet<string>,
  holidayNoteByIso: Readonly<Record<string, string>> | undefined
): string {
  if (!iso || !holidayIsoSet.has(iso)) return '';
  return (holidayNoteByIso?.[iso] ?? '').trim();
}

function stickyPaperStyle(color: string | undefined): React.CSSProperties | undefined {
  if (!color) return undefined;
  return { ['--todo-postit-cat' as string]: color };
}

function autoGrowStickyTextarea(el: HTMLTextAreaElement | null): void {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
}

function StickyNoteCard({
  note,
  category,
  customCategories,
  dateLabel,
  interactive,
  onChangeBody,
  onChangeCategory,
  onLeaveCard,
}: {
  note: DailyWeekNoteDraft;
  category: DailyNoteCategoryRow | undefined;
  customCategories: DailyNoteCategoryRow[];
  dateLabel: string;
  interactive: boolean;
  onChangeBody: (body: string) => void;
  onChangeCategory: (categoryId: string) => void;
  onLeaveCard?: () => void;
}) {
  const hasBody = note.body.trim().length > 0;
  const showCategory = hasBody ? category : undefined;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const catOptions =
    category && !customCategories.some((c) => c.id === category.id)
      ? [category, ...customCategories]
      : customCategories;

  useLayoutEffect(() => {
    autoGrowStickyTextarea(textareaRef.current);
  }, [note.body]);

  return (
    <div
      className={
        'cp-dash-weekly-sticky' +
        (hasBody ? ' is-filled' : ' is-empty') +
        (showCategory ? ' has-cat' : '')
      }
      style={stickyPaperStyle(showCategory?.color)}
      onBlur={(e) => {
        const next = e.relatedTarget as HTMLElement | null;
        if (next && e.currentTarget.contains(next)) return;
        if (next?.closest('.cp-todo-note-row-category-menu')) return;
        onLeaveCard?.();
      }}
    >
      <DailyNoteCategoryDropdown
        variant="pill"
        categories={catOptions}
        value={showCategory ? note.category_id : null}
        disabled={!interactive || catOptions.length === 0}
        ariaLabel={`${dateLabel} 쪽지 카테고리`}
        isDefaultName={isDefaultDailyCategoryName}
        onChange={onChangeCategory}
      />
      <textarea
        ref={textareaRef}
        className="cp-dash-weekly-sticky-textarea"
        value={note.body}
        rows={1}
        disabled={!interactive}
        placeholder=""
        aria-label={`${dateLabel} 카테고리 쪽지`}
        onChange={(e) => {
          autoGrowStickyTextarea(e.currentTarget);
          onChangeBody(e.target.value);
        }}
      />
    </div>
  );
}

function DayNoteBlock({
  dateIso,
  weekdayEn,
  holidayName,
  notes,
  savedNotes,
  categories,
  onChangeNotes,
  contentsHidden,
  onUnlockRequest,
  interactive,
}: {
  dateIso: string;
  weekdayEn: string;
  holidayName: string;
  notes: DailyWeekNoteDraft[];
  savedNotes: DailyWeekNoteDraft[];
  categories: DailyNoteCategoryRow[];
  onChangeNotes: (notes: DailyWeekNoteDraft[]) => void;
  contentsHidden?: boolean;
  onUnlockRequest?: () => void;
  interactive: boolean;
}) {
  const isToday = Boolean(dateIso) && dateIso === toISODate(new Date());
  const hideBody = Boolean(contentsHidden) && dateHasSavedWeekNoteContent(savedNotes, dateIso);
  const defaultCategoryId = defaultDailyCategoryId(categories);
  const customCats = useMemo(() => customDailyCategories(categories), [categories]);
  const firstCustom = firstCustomDailyCategory(categories);
  const categoryById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);
  const general = generalNoteForDate(notes, dateIso, defaultCategoryId);
  const stickies = stickyNotesForDate(notes, dateIso, defaultCategoryId);
  const [emptySlotId, setEmptySlotId] = useState(() => `__draft-${crypto.randomUUID()}`);
  const needEmptySlot = shouldAppendWeeklyStickyComposer(stickies, emptySlotId);
  const stickySlots: DailyWeekNoteDraft[] = needEmptySlot
    ? [
        ...stickies,
        {
          id: emptySlotId,
          note_date: dateIso,
          body: '',
          category_id: null,
          persisted: false,
        },
      ]
    : stickies;

  const dateLabel = formatDashboardWeekMmDd(dateIso);
  const headRef = useRef<HTMLElement | null>(null);
  const stickiesRef = useRef<HTMLDivElement | null>(null);

  /** 스크롤 상·하 = 요일 테두리, 첫 쪽지 상단은 날짜 헤더 아래(수업 보기 TODO와 동일) */
  useLayoutEffect(() => {
    const head = headRef.current;
    const stickies = stickiesRef.current;
    if (!head || !stickies) return;
    const apply = () => {
      stickies.style.setProperty('--cp-dash-week-sticky-lead', `${head.offsetHeight}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(head);
    return () => ro.disconnect();
  }, [dateIso, weekdayEn, holidayName, hideBody]);

  return (
    <div className="cp-dash-weekly-day-content cp-dash-weekly-day-content--notes">
      <header ref={headRef} className="cp-dash-weekly-day-head">
        <span
          className={'cp-dash-weekly-day-date' + (isToday ? ' is-today' : '')}
          aria-current={isToday ? 'date' : undefined}
        >
          {dateLabel}
        </span>
        <span className="cp-dash-weekly-day-en">{weekdayEn}</span>
        {holidayName ? (
          <span className="cp-dash-weekly-day-holiday-name">{holidayName}</span>
        ) : null}
      </header>
      {hideBody ? (
        <DailyNoteLockedPreview variant="weekly" onUnlock={onUnlockRequest} />
      ) : (
        <>
          <div className="cp-dash-weekly-note-general">
            <textarea
              className="cp-dash-weekly-note-textarea"
              value={general?.body ?? ''}
              placeholder="메모·기록을 입력하세요"
              rows={6}
              disabled={!dateIso || !interactive}
              aria-label={`${dateLabel} 일반 기록`}
              onChange={(e) => {
                if (!dateIso || !interactive) return;
                onChangeNotes(setGeneralBodyForDate(notes, dateIso, e.target.value, defaultCategoryId));
              }}
            />
          </div>
          <div
            ref={stickiesRef}
            className="cp-dash-weekly-note-stickies"
            aria-label={`${dateLabel} 카테고리 쪽지`}
          >
            {stickySlots.map((slot) => {
              const isEmptySlot = slot.id === emptySlotId;
              const cat = slot.category_id ? categoryById.get(slot.category_id) : undefined;
              return (
                <StickyNoteCard
                  key={slot.id}
                  note={slot}
                  category={cat}
                  customCategories={customCats}
                  dateLabel={dateLabel}
                  interactive={interactive && dateIso.length > 0 && customCats.length > 0}
                  onLeaveCard={() => {
                    if (!isEmptySlot || !slot.body.trim()) return;
                    setEmptySlotId(`__draft-${crypto.randomUUID()}`);
                  }}
                  onChangeBody={(body) => {
                    if (!dateIso || !interactive) return;
                    if (isEmptySlot) {
                      if (!body.trim()) {
                        onChangeNotes(patchStickyNote(notes, slot.id, { body: '' }));
                        return;
                      }
                      const categoryId = slot.category_id || firstCustom?.id;
                      if (!categoryId) return;
                      onChangeNotes(
                        upsertStickyNote(
                          notes,
                          createDailyWeekDraftNote(dateIso, body, categoryId, slot.id)
                        )
                      );
                      return;
                    }
                    onChangeNotes(patchStickyNote(notes, slot.id, { body }));
                  }}
                  onChangeCategory={(categoryId) => {
                    if (!interactive || !dateIso) return;
                    if (isEmptySlot) {
                      onChangeNotes(
                        upsertStickyNote(
                          notes,
                          createDailyWeekDraftNote(dateIso, slot.body, categoryId, slot.id)
                        )
                      );
                      return;
                    }
                    onChangeNotes(patchStickyNote(notes, slot.id, { category_id: categoryId }));
                  }}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/** 위클리 일자별 기록 노트 면 — 수업 위클리와 동일한 요일 블럭 구조 */
const DashboardWeeklyNotesPlanner: React.FC<Props> = ({
  mondayIso,
  holidayIsoSet,
  holidayNoteByIso,
  daysVertical = false,
  notes,
  savedNotes,
  categories,
  onChangeNotes,
  contentsHidden = false,
  onUnlockRequest,
  interactive = true,
}) => {
  const dates = weekDateIsosFromMonday(mondayIso);
  const weekdayBlocks = [
    { iso: dates[0], en: DASHBOARD_WEEKDAY_EN[0] },
    { iso: dates[1], en: DASHBOARD_WEEKDAY_EN[1] },
    { iso: dates[2], en: DASHBOARD_WEEKDAY_EN[2] },
    { iso: dates[3], en: DASHBOARD_WEEKDAY_EN[3] },
    { iso: dates[4], en: DASHBOARD_WEEKDAY_EN[4] },
  ];

  return (
    <div className="cp-dash-weekly cp-dash-weekly--notes" aria-label="위클리 일자별 기록">
      <div className={'cp-dash-weekly-grid' + (daysVertical ? ' is-days-vertical' : '')}>
        {weekdayBlocks.map((b) => {
          const isHoliday = !!b.iso && holidayIsoSet.has(b.iso);
          const holidayName = holidayNameFor(b.iso, holidayIsoSet, holidayNoteByIso);
          return (
            <article
              key={b.en}
              className={'cp-dash-weekly-day' + (isHoliday ? ' is-holiday' : '')}
            >
              <DayNoteBlock
                dateIso={b.iso || ''}
                weekdayEn={b.en}
                holidayName={holidayName}
                notes={notes}
                savedNotes={savedNotes ?? []}
                categories={categories}
                onChangeNotes={onChangeNotes}
                contentsHidden={contentsHidden}
                onUnlockRequest={onUnlockRequest}
                interactive={interactive}
              />
            </article>
          );
        })}

        <article className="cp-dash-weekly-day cp-dash-weekly-day--weekend">
          {[5, 6].map((idx) => {
            const iso = dates[idx];
            const isHoliday = !!iso && holidayIsoSet.has(iso);
            const holidayName = holidayNameFor(iso, holidayIsoSet, holidayNoteByIso);
            return (
              <div
                key={DASHBOARD_WEEKDAY_EN[idx]}
                className={'cp-dash-weekly-weekend-half' + (isHoliday ? ' is-holiday' : '')}
              >
                <DayNoteBlock
                  dateIso={iso || ''}
                  weekdayEn={DASHBOARD_WEEKDAY_EN[idx]}
                  holidayName={holidayName}
                  notes={notes}
                  savedNotes={savedNotes ?? []}
                  categories={categories}
                  onChangeNotes={onChangeNotes}
                  contentsHidden={contentsHidden}
                  onUnlockRequest={onUnlockRequest}
                  interactive={interactive}
                />
              </div>
            );
          })}
        </article>
      </div>
    </div>
  );
};

export default DashboardWeeklyNotesPlanner;
